import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";

/**
 * RecommendationCardHeader Component
 * Shows rank badge, worker info, arrival/departure windows, and drive times
 */
export default function RecommendationCardHeader({
  rank,
  isBestMatch,
  worker,
  proposedStartTime,
  durationMinutes,
  scheduleOpeningDuration,
  bufferMinutes,
  routeData,
  estimatedDriveTimeMinutes,
  isLoadingRoute,
}) {
  // Calculate departure time from proposed start + duration
  const departureTime = new Date(new Date(proposedStartTime).getTime() + durationMinutes * 60000);

  // Calculate total extra time (slack)
  const driveAtoB = routeData?.a_to_b?.duration_minutes || 0;
  const driveBtoC = routeData?.b_to_c?.duration_minutes || 0;
  const totalTimeNeeded = driveAtoB + durationMinutes + driveBtoC + bufferMinutes;
  const extraTime = Math.round(scheduleOpeningDuration - totalTimeNeeded);

  // Calculate days until appointment
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const appointmentDate = new Date(proposedStartTime);
  appointmentDate.setHours(0, 0, 0, 0);
  const daysUntil = Math.round((appointmentDate - today) / (1000 * 60 * 60 * 24));

  const daysText = daysUntil === 0 ? "Today" :
                   daysUntil === 1 ? "Tomorrow" :
                   `In ${daysUntil} days`;
  return (
    <>
      {/* Rank Badge */}
      <Flex justify="space-between" align="start" mb={4}>
        <Box
          px={3}
          py={1}
          borderRadius="full"
          bg={isBestMatch ? "blue.500" : "gray.200"}
          color={isBestMatch ? "white" : "gray.700"}
          fontSize="sm"
          fontWeight="bold"
        >
          #{rank}
        </Box>
        {isBestMatch && (
          <Box
            px={3}
            py={1}
            borderRadius="full"
            bg="green.100"
            color="green.700"
            fontSize="xs"
            fontWeight="medium"
          >
            Best Match
          </Box>
        )}
      </Flex>

      {/* Top Row: Worker, Date, Times, Duration, Travel Time */}
      <Flex gap={4} mb={4} flexWrap="wrap" sx={{ "& > div": { minWidth: { base: "calc(50% - 0.5rem)", sm: "120px" } } }}>
        {/* Worker Info */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "150px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Worker
          </Text>
          <Text fontSize="md" fontWeight="semibold" color="gray.800">
            {worker.name}
          </Text>
        </Box>

        {/* Date */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "150px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Date
          </Text>
          <Text fontSize="md" fontWeight="medium" color="gray.800">
            {new Date(proposedStartTime).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            {daysText}
          </Text>
        </Box>

        {/* Arrival Time */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "120px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Arrival Time
          </Text>
          <Text fontSize="md" fontWeight="medium" color="green.700">
            {new Date(proposedStartTime).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </Text>
        </Box>

        {/* Departure Time */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "120px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Departure Time
          </Text>
          <Text fontSize="md" fontWeight="medium" color="blue.700">
            {departureTime.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </Text>
        </Box>

        {/* Duration */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "150px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Duration
          </Text>
          <Text fontSize="md" fontWeight="medium" color="gray.800">
            {durationMinutes} min
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Extra Time: {extraTime} min
          </Text>
        </Box>

        {/* Total Travel Time */}
        <Box flex="1" minW={{ base: "calc(50% - 0.5rem)", sm: "150px" }}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Total Travel Time
          </Text>
          {routeData ? (
            <>
              <Text fontSize="md" color="gray.700" fontWeight="medium">
                {((routeData.a_to_b?.duration_minutes || 0) +
                  (routeData.b_to_c?.duration_minutes || 0)).toFixed(0)} minutes
              </Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                A→B: {routeData.a_to_b?.duration_minutes?.toFixed(0) || '0'} min
                {' • '}
                B→C: {routeData.b_to_c?.duration_minutes?.toFixed(0) || '0'} min
              </Text>
            </>
          ) : estimatedDriveTimeMinutes > 0 ? (
            <>
              <Text fontSize="md" color="gray.700">
                ~{Math.round(estimatedDriveTimeMinutes)} minutes
              </Text>
            </>
          ) : (
            <Text fontSize="md" color="gray.500">
              N/A
            </Text>
          )}
        </Box>
      </Flex>
    </>
  );
}
